import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '../components/ui/dialog';
import {
  Users,
  Plus,
  MessageSquare,
  Globe,
  Lock,
  UserPlus,
  Settings,
  TrendingUp,
  Clock,
  ArrowUp,
  ArrowDown,
  Share2,
  MoreHorizontal,
  UserMinus,
  Shield,
  ChevronLeft
} from 'lucide-react';
import GuestBanner from '../components/GuestBanner';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import usePageTitle from '../hooks/usePageTitle';

const API = process.env.REACT_APP_BACKEND_URL;

// Post card component
const PostCard = ({ post, onVote, userId }) => {
  const navigate = useNavigate();

  const handleVote = async (e, value) => {
    e.stopPropagation();
    onVote(post.id, value);
  };

  return (
    <Card 
      className="hover:shadow-sm transition-all cursor-pointer"
      onClick={() => navigate(`/communities/${post.subgroup_id}/posts/${post.id}`)}
      data-testid={`post-${post.id}`}
    >
      <CardContent className="p-4">
        <div className="flex gap-4">
          {/* Vote buttons */}
          <div className="flex flex-col items-center gap-1">
            <button
              onClick={(e) => handleVote(e, post.user_vote === 1 ? 0 : 1)}
              className={`p-1 rounded hover:bg-muted transition-colors ${
                post.user_vote === 1 ? 'text-primary' : 'text-muted-foreground'
              }`}
              data-testid={`upvote-${post.id}`}
            >
              <ArrowUp className="w-5 h-5" />
            </button>
            <span className={`font-medium text-sm ${
              post.score > 0 ? 'text-primary' : post.score < 0 ? 'text-destructive' : 'text-muted-foreground'
            }`}>
              {post.score}
            </span>
            <button
              onClick={(e) => handleVote(e, post.user_vote === -1 ? 0 : -1)}
              className={`p-1 rounded hover:bg-muted transition-colors ${
                post.user_vote === -1 ? 'text-destructive' : 'text-muted-foreground'
              }`}
              data-testid={`downvote-${post.id}`}
            >
              <ArrowDown className="w-5 h-5" />
            </button>
          </div>

          {/* Post content */}
          <div className="flex-1 min-w-0">
            {post.is_pinned && (
              <Badge variant="outline" className="mb-2 text-xs">📌 Pinned</Badge>
            )}
            <h3 className="font-semibold mb-1 line-clamp-2">{post.title}</h3>
            <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
              {post.content}
            </p>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Avatar className="w-4 h-4">
                  <AvatarImage src={post.author_avatar} />
                  <AvatarFallback>{post.author_username?.[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>
                {post.author_username}
              </span>
              <span>{formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}</span>
              <span className="flex items-center gap-1">
                <MessageSquare className="w-3 h-3" />
                {post.comment_count} comments
              </span>
            </div>
          </div>

          {post.image_url && (
            <img 
              src={post.image_url} 
              alt="" 
              className="w-20 h-20 object-cover rounded-md shrink-0"
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Create post dialog
const CreatePostDialog = ({ subgroupId, onPostCreated }) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;

    try {
      setLoading(true);
      await axios.post(`${API}/api/subgroups/${subgroupId}/posts`, {
        title,
        content,
        subgroup_id: subgroupId
      });
      
      toast.success('Post created successfully');
      setTitle('');
      setContent('');
      setOpen(false);
      onPostCreated();
    } catch (error) {
      toast.error('Failed to create post');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="create-post-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Post
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create a Post</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Input
              placeholder="Post title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              minLength={5}
              maxLength={300}
              data-testid="post-title-input"
            />
          </div>
          <div>
            <Textarea
              placeholder="What's on your mind?"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              required
              minLength={10}
              rows={6}
              data-testid="post-content-input"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="submit-post-btn">
              {loading ? 'Posting...' : 'Post'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const SubGroupDetail = () => {
  const { subgroupId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [subgroup, setSubgroup] = useState(null);
  const [posts, setPosts] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('new');
  const [activeTab, setActiveTab] = useState('posts');

  // Set dynamic page title
  usePageTitle(subgroup?.name || 'Community');

  const fetchSubgroup = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/subgroups/${subgroupId}`);
      setSubgroup(response.data);
    } catch (error) {
      console.error('Error fetching subgroup:', error);
      if (error.response?.status === 404) {
        navigate('/communities');
      }
    }
  }, [subgroupId, navigate]);

  const fetchPosts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/subgroups/${subgroupId}/posts?sort=${sortBy}`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  }, [subgroupId, sortBy]);

  const fetchMembers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/subgroups/${subgroupId}/members?limit=10`);
      setMembers(response.data);
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  }, [subgroupId]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchSubgroup();
      await Promise.all([fetchPosts(), fetchMembers()]);
      setLoading(false);
    };
    loadData();
  }, [fetchSubgroup, fetchPosts, fetchMembers]);

  const handleJoin = async () => {
    if (!user) {
      navigate('/login', { state: { from: `/communities/${subgroupId}` } });
      return;
    }
    try {
      await axios.post(`${API}/api/subgroups/${subgroupId}/join`, {});
      toast.success('Joined community!');
      fetchSubgroup();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join');
    }
  };

  const handleLeave = async () => {
    try {
      await axios.delete(`${API}/api/subgroups/${subgroupId}/leave`);
      toast.success('Left community');
      fetchSubgroup();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to leave');
    }
  };

  const handleVote = async (postId, value) => {
    if (!user) {
      navigate('/login', { state: { from: `/communities/${subgroupId}` } });
      return;
    }
    try {
      const response = await axios.post(
        `${API}/api/subgroups/${subgroupId}/posts/${postId}/vote?value=${value}`,
        {});
      
      // Update post in list
      setPosts(posts.map(p => 
        p.id === postId 
          ? { ...p, score: response.data.score, user_vote: response.data.user_vote }
          : p
      ));
    } catch (error) {
      toast.error('Failed to vote');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 max-w-6xl">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-1/4" />
            <div className="h-4 bg-muted rounded w-1/2" />
            <div className="grid md:grid-cols-3 gap-6">
              <div className="md:col-span-2 space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-32 bg-muted rounded" />
                ))}
              </div>
              <div className="h-64 bg-muted rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!subgroup) return null;

  return (
    <div className="min-h-screen bg-background">
      {/* Header Banner */}
      <div 
        className="py-8"
        style={{ backgroundColor: `${subgroup.color}15` }}
      >
        <div className="container mx-auto px-4 max-w-6xl">
          <Link 
            to="/communities" 
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to Communities
          </Link>
          
          <div className="flex items-start gap-4">
            <div 
              className="w-16 h-16 md:w-20 md:h-20 rounded-xl flex items-center justify-center text-3xl shrink-0"
              style={{ backgroundColor: `${subgroup.color}30` }}
            >
              {subgroup.icon_emoji}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-2xl md:text-3xl font-heading font-bold">{subgroup.name}</h1>
                {subgroup.visibility === 'public' && <Globe className="w-4 h-4 text-emerald-600" />}
                {subgroup.visibility === 'private' && <Lock className="w-4 h-4 text-muted-foreground" />}
                {subgroup.is_example && (
                  <Badge
                    variant="outline"
                    className="text-xs border-amber-400 text-amber-700 bg-amber-50"
                    data-testid="example-badge-detail"
                    title="Example community seeded by the team"
                  >
                    Example community
                  </Badge>
                )}
              </div>
              {subgroup.is_example && (
                <div className="mb-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="example-notice-detail">
                  This is an <strong>example community</strong> created by the team to show what&rsquo;s possible. Feel free to join and post — or <Link to="/communities/create" className="underline font-medium">create your own community</Link> on any topic you care about.
                </div>
              )}
              <p className="text-muted-foreground mb-3">{subgroup.description}</p>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {subgroup.member_count} members
                </span>
                <span className="flex items-center gap-1">
                  <MessageSquare className="w-4 h-4" />
                  {subgroup.post_count} posts
                </span>
                {subgroup.club_name && (
                  <Link to={`/club/${subgroup.club_slug}`} className="hover:text-primary">
                    Part of {subgroup.club_name}
                  </Link>
                )}
              </div>
            </div>
            <div className="shrink-0 flex gap-2">
              {subgroup.is_member ? (
                <>
                  <Button variant="outline" onClick={handleLeave} data-testid="leave-btn">
                    <UserMinus className="w-4 h-4 mr-2" />
                    Leave
                  </Button>
                  {subgroup.is_moderator && (
                    <Button variant="outline" onClick={() => navigate(`/communities/${subgroupId}/settings`)}>
                      <Settings className="w-4 h-4" />
                    </Button>
                  )}
                </>
              ) : (
                <Button onClick={handleJoin} data-testid="join-btn">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Join Community
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-6 max-w-6xl">
        <GuestBanner message="Join this community to post and interact" />

        <div className="grid md:grid-cols-3 gap-6">
          {/* Main content */}
          <div className="md:col-span-2 space-y-4">
            {/* Post controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button
                  variant={sortBy === 'new' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setSortBy('new')}
                >
                  <Clock className="w-4 h-4 mr-1" />
                  New
                </Button>
                <Button
                  variant={sortBy === 'top' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setSortBy('top')}
                >
                  <TrendingUp className="w-4 h-4 mr-1" />
                  Top
                </Button>
                <Button
                  variant={sortBy === 'hot' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setSortBy('hot')}
                >
                  🔥 Hot
                </Button>
              </div>
              {user && subgroup.allow_posts && (
                <CreatePostDialog 
                  subgroupId={subgroupId} 
                  onPostCreated={fetchPosts}
                />
              )}
            </div>

            {/* Posts */}
            {posts.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">No posts yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Be the first to start a discussion!
                  </p>
                  {user && subgroup.allow_posts && (
                    <CreatePostDialog 
                      subgroupId={subgroupId} 
                      onPostCreated={fetchPosts}
                    />
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {posts.map(post => (
                  <PostCard 
                    key={post.id} 
                    post={post} 
                    onVote={handleVote}
                    userId={user?.id}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* About */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">About</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">{subgroup.description}</p>
                
                {subgroup.rules?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Rules</h4>
                    <ol className="text-sm space-y-1 list-decimal list-inside text-muted-foreground">
                      {subgroup.rules.map((rule, i) => (
                        <li key={rule}>{rule}</li>
                      ))}
                    </ol>
                  </div>
                )}

                <div className="pt-2 border-t text-sm text-muted-foreground">
                  Created {formatDistanceToNow(new Date(subgroup.created_at), { addSuffix: true })}
                </div>
              </CardContent>
            </Card>

            {/* Members */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Members</CardTitle>
                <Badge variant="secondary">{subgroup.member_count}</Badge>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {members.map(member => (
                    <div key={member.id} className="flex items-center gap-2">
                      <Avatar className="w-8 h-8">
                        <AvatarFallback>{member.username?.[0]?.toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{member.username}</p>
                        {member.role !== 'member' && (
                          <Badge variant="outline" className="text-xs">
                            {member.role === 'admin' && <Shield className="w-3 h-3 mr-1" />}
                            {member.role}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubGroupDetail;
